import React, { useState } from "react";
import { useDispatch } from "react-redux";
import CloseIcon from "../Assets/svgs/CloseIcon";
import { Loader } from "../Common/Loader";
import {
  getPhoneNumberLimits,
  handlePhoneNoChange,
  phoneNumberValidation,
} from "../Common/Validations";
import { importPhoneNumber } from "../Redux/Slice/PhoneNumbers/ImportPhoneNumberSlice";
import { ErrorModel } from "./ErrorModel";
import { ALL_CALLING_CODES } from "../Common/Constants";
import { maskNoByCallingCode } from "../Common/utils";
import CountryPhoneInput from "../Common/CountryPhoneInput";

const ImportPhoneNumberModal = ({ closePopup, onSuccess }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    twilioCountryCode: "+61",
    twilioPhoneNo: "",
    twilioSSID: "",
    twilioAuthToken: "",
    label: "",
  });
  const [formError, setFormError] = useState({
    twilioPhoneError: "",
    twilioPhoneError: "",
    labelError: "",
  });
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });

  const handleChange = async (e) => {
    let { name, value } = e.target;
    value = value.trimStart();
    if (name == "twilioPhoneNo") {
      handlePhoneNoChange(
        e,
        formData.twilioCountryCode,
        () =>
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          })),
        (newError) =>
          setFormError({ ...formError, twilioPhoneError: newError }),
        (isChanged) => {},
        false
      );
    } else if (name == "twilioCountryCode") {
      let formatted = formData.twilioPhoneNo;
      setFormData((prev) => ({
        ...prev,
        twilioPhoneNo: formatted,
        [name]: value,
      }));
      const minDigits = getPhoneNumberLimits(value)?.min ?? 10;
      const rawDigits = formData.twilioPhoneNo?.replace(/\D/g, "");
      const isValid = phoneNumberValidation(rawDigits, value);
      setFormError({
        ...formError,
        twilioPhoneError:
          isValid || !value || !formData.twilioPhoneNo
            ? ""
            : rawDigits.length < minDigits
            ? `Phone number must be at least ${minDigits} digits long.`
            : "Please enter a valid phone number",
      });
    } else if (name === "label") {
      let capitalized = value.charAt(0).toString().toUpperCase();
      value = capitalized + value.substring(1);
      const isValid = !(value?.length < 3);
      if (isValid || value.trim() === "") {
        setFormError({ ...formError, labelError: "" });
      } else {
        setFormError({
          ...formError,
          labelError: "Subject must be between 3 and 100 characters long",
        });
      }
      setFormData({
        ...formData,
        label: value,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const handleSubmit = async () => {
    let body = {
      provider: "twilio",
      number: `${formData.twilioCountryCode}${formData.twilioPhoneNo?.replace(
        /\D/g,
        ""
      )}`,
      name: formData.label,
      twilioAccountSid: formData.twilioSSID,
      twilioAuthToken: formData.twilioAuthToken,
    };
    setIsLoading(true);
    try {
      const result = await dispatch(importPhoneNumber(body));
      if (
        importPhoneNumber.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        onSuccess("Phone Number added Successfully!");
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (errorModal.visible) {
    return <ErrorModel message={errorModal.message} />;
  }

  return (
    <div className="modal modal--createAssistant modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--md">
          <div className="modal__header ">
            <h1 className="modal__header--title">Import Number</h1>
            <p className="para">Import your number here.</p>
            <button
              className="btn btn__transparent btnClose"
              onClick={closePopup}
            >
              <CloseIcon />
            </button>
          </div>
          <div className="modal__body  modal-body-left">
            <div className="scheduleCall">
              <div className="scheduleCall__row">
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label for="twilioPhoneNo" className="form__label">
                      Twilio Phone Number
                      <span style={{ color: "red" }}> * </span>
                    </label>
                    <CountryPhoneInput
                      countryCodeFieldName="twilioCountryCode"
                      phoneFieldName="twilioPhoneNo"
                      countryCode={formData.twilioCountryCode}
                      phoneNumber={formData.twilioPhoneNo}
                      onCountryChange={handleChange}
                      onPhoneChange={handleChange}
                    />
                    {formError.twilioPhoneError ? (
                      <div className="form__error">
                        {formError.twilioPhoneError}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label
                      for="subject"
                      className="form__label form__label--flex"
                    >
                      <span>
                        Twilio Account SSID
                        <sup className="form__label__asterisk">*</sup>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="twilioSSID"
                      name="twilioSSID"
                      value={formData.twilioSSID}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="SSID"
                      maxLength={100}
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label
                      for="subject"
                      className="form__label form__label--flex"
                    >
                      <span>
                        Twilio Auth Token
                        <sup className="form__label__asterisk">*</sup>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="twilioAuthToken"
                      name="twilioAuthToken"
                      value={formData.twilioAuthToken}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="Auth Token"
                      maxLength={100}
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label
                      for="subject"
                      className="form__label form__label--flex"
                    >
                      <span>
                        Label
                        <sup className="form__label__asterisk">*</sup>
                      </span>
                    </label>
                    <input
                      type="text"
                      id="label"
                      name="label"
                      value={formData.label}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="Label for phone number"
                      maxLength={100}
                    />
                  </div>
                  {formError.labelError ? (
                    <div className="form__error">{formError.labelError}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={closePopup}>
                Cancel
              </button>
              <button
                disabled={
                  !formData.label ||
                  !formData.twilioAuthToken ||
                  !formData.twilioCountryCode ||
                  !formData.twilioPhoneNo ||
                  !formData.twilioSSID ||
                  formError.labelError ||
                  formError.twilioPhoneError
                }
                className="btn btn__primary"
                onClick={handleSubmit}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default ImportPhoneNumberModal;
